import { X } from '@ui/components/icons';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { Portal } from '@ui/lib/park/portal';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import type { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3/types';
import { isNil } from 'lodash-es';
import { useState } from 'react';
import { HStack, Stack } from 'styled-system/jsx';

import { TradeLeverageBadge } from '../LeverageBadge';
import { EditDecreaseOrderForm } from './EditDecreaseOrderForm';

export const EditDecreaseOrderOverlay = ({
  children,
  order,
  position,
  onOpenChange,
  ...rest
}: Omit<Dialog.RootProps, 'type'> & {
  order: DecreaseOrderRow;
  position: Position;
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );
  const pairMarketPrice = usePriceLatest(position.pair.baseSymbol);

  if (isNil(collateralMarketPrice) || isNil(pairMarketPrice)) return null;

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      {...rest}
      open={isOpen}
      onOpenChange={(details) => {
        setIsOpen(details.open);
        onOpenChange?.(details);
      }}
    >
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            w="560px"
            maxW="calc(100vw - 32px)"
            maxH="calc(100vh - 48px)"
            overflow="hidden"
          >
            <Stack gap={0}>
              <HStack
                px="4"
                py="3"
                justifyContent="space-between"
                borderBottom="1px solid"
                borderBottomColor="v2.border.default"
              >
                <Stack gap="0" minW={0}>
                  <HStack gap="2">
                    <Text textStyle="headings.default">
                      Edit {order.kind}
                    </Text>
                    <TradeLeverageBadge genericTradeParams={position} />
                  </HStack>
                  <Text textStyle="v2.meta" color="v2.text.muted">
                    {position.isLong ? 'Long' : 'Short'}{' '}
                    {position.pair.baseName}
                  </Text>
                </Stack>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px" color="v2.text.muted">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </HStack>
              <Stack maxH="520px" overflowY="auto" p="3">
                <EditDecreaseOrderForm
                  order={order}
                  genericTradeParams={position}
                  collateralMarketPrice={collateralMarketPrice}
                  pairMarketPrice={pairMarketPrice}
                  onDone={() => setIsOpen(false)}
                />
              </Stack>
            </Stack>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
